import ConsentService from '@consent/scripts/services/ConsentService';
import type {ConsentChangeEventDetail} from '@consent/scripts/types/ConsentChangeEvent';
import {ConsentValue} from '@consent/scripts/types/ConsentChangeEvent';
import {setDyEventConsent} from '@dynamic-yield/scripts/components/dynamic-yield/dy-event-sender';

const DY_ID_SERVER_COOKIE_NAME = '_dyid_server';
const DY_CONSENT_COOKIE_NAME = '_dy_consent';
const DY_CONSENT_GRANTED = '1';
const DY_CONSENT_DENIED = '0';
const DY_CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;


export type DyConsentOptions = {
    vendorId?: string;
    eventName: string;
    hasConsent: boolean;
    cookieDomain?: string;
};

export function observeDyConsent({
    vendorId,
    eventName,
    hasConsent,
    cookieDomain,
}: DyConsentOptions): void {
    let lastConsent = hasConsent;

    const applyConsent = (consent: boolean) => {
        writeDyConsentCookie(consent, cookieDomain);

        if (consent === lastConsent) {
            return;
        }

        lastConsent = consent;
        publishDyConsent(consent);
        setDyEventConsent(consent);

        if (!consent) {
            expireDyIdServerCookie(cookieDomain);
        }
    };

    document.addEventListener(eventName, (event: Event) => {
        const consent = extractDyConsent(event, vendorId);
        if (consent === null) {
            return;
        }

        applyConsent(consent);
    });

    if (vendorId) {
        void reconcileWithHurra(vendorId, applyConsent);
    }
}

async function reconcileWithHurra(vendorId: string, applyConsent: (consent: boolean) => void): Promise<void> {
    const vendorConsent = await ConsentService.getExternalVendorConsent(vendorId);
    if (vendorConsent === undefined) {
        return;
    }

    applyConsent(vendorConsent === ConsentValue.ACCEPTED);
}

function writeDyConsentCookie(consent: boolean, cookieDomain: string | undefined): void {
    const value = consent ? DY_CONSENT_GRANTED : DY_CONSENT_DENIED;
    const domainPart = cookieDomain ? `; Domain=${cookieDomain}` : '';
    const securePart = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${DY_CONSENT_COOKIE_NAME}=${value}; Max-Age=${DY_CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${domainPart}${securePart}`;
}

/**
 * The documented way to tell Dynamic Yield about a consent decision, before their scripts load and
 * whenever it changes afterwards: https://dy.dev/docs/active-cookie-consent. The object is mutated
 * rather than replaced, because their scripts read it while they execute and may hold the reference.
 */
function publishDyConsent(accepted: boolean): void {
    const dy = (window.DY = window.DY ?? {});
    if (dy.userActiveConsent) {
        dy.userActiveConsent.accepted = accepted;
        return;
    }
    dy.userActiveConsent = {accepted};
}

function expireDyIdServerCookie(cookieDomain: string | undefined): void {
    document.cookie = `${DY_ID_SERVER_COOKIE_NAME}=; Max-Age=0; Path=/`;

    if (!cookieDomain) {
        return;
    }

    document.cookie = `${DY_ID_SERVER_COOKIE_NAME}=; Max-Age=0; Path=/; Domain=${cookieDomain}`;
}

function extractDyConsent(event: Event, vendorId: string | undefined): boolean | null {
    if (!vendorId) {
        return null;
    }

    const detail = (event as CustomEvent<ConsentChangeEventDetail>).detail;
    const vendorConsent = detail?.vendors?.PERSONALIZATION?.[vendorId];
    if (vendorConsent === undefined) {
        return null;
    }
    return vendorConsent === ConsentValue.ACCEPTED;
}
